import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { QRCodeCanvas } from 'qrcode.react';
import axios from 'axios';
import { toast } from 'sonner';
import { BarChart3, Check, Gift, Loader2, Vote, Lock, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getDeviceId = () => {
  let id = localStorage.getItem('poll_device_id');
  if (!id) {
    id = 'dev_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem('poll_device_id', id);
  }
  return id;
};

const PublicPoll = () => {
  const { pollId } = useParams();
  const [poll, setPoll] = useState(null);
  const [loading, setLoading] = useState(true);
  const [voting, setVoting] = useState(false);
  const [hasVoted, setHasVoted] = useState(false);
  const [votedOptionId, setVotedOptionId] = useState(null);
  const [redemptionCode, setRedemptionCode] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [selected, setSelected] = useState(null);
  const [feedback, setFeedback] = useState('');
  const qrWrapRef = useRef(null);

  const deviceId = getDeviceId();
  const votedKey = `poll_voted_${pollId}`;

  const fetchPoll = async () => {
    try {
      const res = await axios.get(`${API}/polls/${pollId}`);
      setPoll(res.data);
      const stored = localStorage.getItem(votedKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        setHasVoted(true);
        setVotedOptionId(parsed.optionId);
        setRedemptionCode(parsed.code || null);
      }
    } catch (e) {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPoll();
    // eslint-disable-next-line
  }, [pollId]);

  const castVote = async () => {
    if (!selected) {
      toast.error('Please pick an option');
      return;
    }
    setVoting(true);
    try {
      const res = await axios.post(`${API}/polls/${pollId}/vote`, {
        option_id: selected,
        device_id: deviceId,
        feedback: feedback.trim() || null,
      });
      setPoll(res.data);
      setHasVoted(true);
      setVotedOptionId(res.data.voted_option_id);
      setRedemptionCode(res.data.redemption_code || null);
      localStorage.setItem(
        votedKey,
        JSON.stringify({ optionId: res.data.voted_option_id, code: res.data.redemption_code || null })
      );
      if (!res.data.already_voted) toast.success('Thanks for your vote!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit vote');
    } finally {
      setVoting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#EAB308] animate-spin" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center px-6">
        <div className="text-center" data-testid="poll-not-found">
          <div className="bg-[#18181b] border border-[#27272a] rounded-2xl p-8 max-w-sm">
            <BarChart3 className="w-12 h-12 text-[#52525b] mx-auto mb-4" />
            <h1 className="text-xl font-heading font-bold text-white mb-2">Poll Not Found</h1>
            <p className="text-[#a1a1aa] text-sm">This poll may have been removed or the link is incorrect.</p>
          </div>
        </div>
      </div>
    );
  }

  const closed = poll.status === 'closed';
  const showResults = hasVoted || closed;
  const redeemUrl = redemptionCode ? `${window.location.origin}/redeem/${redemptionCode}` : null;

  const downloadQR = () => {
    const canvas = qrWrapRef.current?.querySelector('canvas');
    if (!canvas) return toast.error('Could not prepare the image');
    const link = document.createElement('a');
    link.download = `rafflesrus-reward-${redemptionCode}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
    toast.success('Saved to your device');
  };

  return (
    <div className="min-h-screen bg-[#09090b] py-10 px-5">
      <div className="max-w-md mx-auto space-y-6">
        {/* Brand header */}
        <div className="text-center">
          <span className="text-xs uppercase tracking-widest text-[#EAB308] font-semibold">RafflesRUs Poll</span>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 90 }}
          className="bg-[#18181b] border border-[#27272a] rounded-3xl p-6 shadow-[0_4px_30px_rgba(0,0,0,0.5)]"
        >
          <div className="flex items-start gap-3 mb-6">
            <div className="bg-[#EAB308]/15 p-2 rounded-xl shrink-0">
              <Vote className="w-5 h-5 text-[#EAB308]" />
            </div>
            <h1 className="text-lg font-heading font-bold text-white leading-snug" data-testid="poll-question">
              {poll.question}
            </h1>
          </div>

          {closed && !hasVoted && (
            <div className="flex items-center gap-2 bg-[#27272a] rounded-lg px-3 py-2 mb-4 text-[#a1a1aa] text-sm">
              <Lock className="w-4 h-4" /> This poll is closed. Here are the results.
            </div>
          )}

          {/* Voting view */}
          {!showResults && (
            <div className="space-y-3" data-testid="poll-options">
              {poll.options.map((opt) => (
                <button
                  key={opt.id}
                  data-testid={`poll-option-${opt.id}`}
                  onClick={() => setSelected(opt.id)}
                  className={`w-full text-left px-4 py-3.5 rounded-xl border transition-colors flex items-center gap-3 ${
                    selected === opt.id
                      ? 'border-[#EAB308] bg-[#EAB308]/10'
                      : 'border-[#27272a] bg-[#09090b] hover:border-[#52525b]'
                  }`}
                >
                  <span
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                      selected === opt.id ? 'border-[#EAB308]' : 'border-[#52525b]'
                    }`}
                  >
                    {selected === opt.id && <span className="w-2.5 h-2.5 rounded-full bg-[#EAB308]" />}
                  </span>
                  <span className="text-white text-sm font-medium">{opt.text}</span>
                </button>
              ))}

              <div className="pt-1">
                <label className="text-sm text-[#a1a1aa] mb-1.5 block">Anything else to add? <span className="text-[#52525b]">(optional)</span></label>
                <Textarea
                  data-testid="poll-feedback-input"
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder="Share any extra thoughts — only the team sees this"
                  className="bg-[#09090b] border-[#27272a] text-white min-h-[64px]"
                />
              </div>

              <Button
                data-testid="poll-vote-btn"
                onClick={castVote}
                disabled={voting || !selected}
                className="w-full mt-2 bg-[#EAB308] hover:bg-[#CA8A04] text-black font-bold rounded-full h-12"
              >
                {voting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit Vote'}
              </Button>
              <p className="text-center text-xs text-[#52525b]">Anonymous · one vote per device</p>
            </div>
          )}

          {/* Results view */}
          {showResults && (
            <div className="space-y-4" data-testid="poll-results">
              {hasVoted && (
                <div className="flex items-center gap-2 text-[#10b981] text-sm font-medium">
                  <Check className="w-4 h-4" /> Your vote is in
                </div>
              )}
              {poll.options.map((opt) => {
                const isChoice = opt.id === votedOptionId;
                return (
                  <div key={opt.id} data-testid={`poll-result-${opt.id}`}>
                    <div className="flex justify-between items-center mb-1.5">
                      <span className={`text-sm font-medium ${isChoice ? 'text-[#EAB308]' : 'text-white'}`}>
                        {opt.text} {isChoice && '·'}
                      </span>
                      <span className="text-sm text-[#a1a1aa] tabular-nums">
                        {opt.percentage}% <span className="text-[#52525b]">({opt.votes})</span>
                      </span>
                    </div>
                    <div className="h-2.5 bg-[#27272a] rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${opt.percentage}%` }}
                        transition={{ duration: 0.6, ease: 'easeOut' }}
                        className={`h-full rounded-full ${isChoice ? 'bg-[#EAB308]' : 'bg-[#A855F7]'}`}
                      />
                    </div>
                  </div>
                );
              })}
              <p className="text-center text-xs text-[#52525b] pt-1">
                {poll.total_votes} {poll.total_votes === 1 ? 'vote' : 'votes'} total
              </p>
            </div>
          )}
        </motion.div>

        {/* Reward QR */}
        <AnimatePresence>
          {hasVoted && redemptionCode && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, type: 'spring', damping: 20 }}
              className="bg-gradient-to-br from-[#18181b] to-[#1f1a08] border border-[#EAB308]/40 rounded-3xl p-6 text-center"
              data-testid="poll-reward-qr"
            >
              <div className="flex items-center justify-center gap-2 mb-1">
                <Gift className="w-5 h-5 text-[#EAB308]" />
                <h2 className="text-base font-heading font-bold text-white">Your Reward</h2>
              </div>
              <p className="text-[#a1a1aa] text-sm mb-4">Show this QR code to staff to claim your deal.</p>
              <div ref={qrWrapRef} className="bg-white p-4 rounded-2xl inline-block">
                <QRCodeCanvas value={redeemUrl} size={180} level="M" />
              </div>
              <p className="mt-4 text-xs uppercase tracking-widest text-[#52525b]">Code</p>
              <p className="text-2xl font-heading font-bold text-[#EAB308] tracking-wider" data-testid="poll-reward-code">
                {redemptionCode}
              </p>
              <Button
                data-testid="download-reward-qr-btn"
                onClick={downloadQR}
                className="mt-4 w-full bg-white/[0.08] hover:bg-white/[0.14] text-white font-semibold rounded-full h-11"
              >
                <Download className="w-4 h-4 mr-2" /> Save QR code
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default PublicPoll;
